import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Seo from "../components/Seo";
import BookingCta from "../components/BookingCta";
import Pricing from "../components/Pricing";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useSiteConfig } from "../lib/site";

const TECHNIQUES = [
  {
    id: "lymphatic",
    title: "Lymphatic drainage",
    body: "Very light, rhythmic touch that supports the body's natural lymph flow. Many clients choose it while recovering from surgery, or when living with swelling, discomfort or pain - the gentle pace makes it comfortable when deeper work would not be. If you are recovering from an operation, please check with your doctor first and mention it when you schedule.",
  },
  {
    id: "neuromuscular",
    title: "Neuromuscular work",
    body: "Focused, hands-on work with areas of muscular tension. Pressure is always discussed together, and adjusted to what feels right for you.",
  },
  {
    id: "craniosacral",
    title: "Craniosacral work",
    body: "A gentle-touch approach for clients who prefer a quieter session. Many people choose it simply because they want stillness and a lighter contact.",
  },
  {
    id: "reflexology",
    title: "Reflexology",
    body: "Work with pressure points on the feet or hands - a good choice if you would rather stay fully clothed and keep the session to those areas.",
  },
  {
    id: "reiki",
    title: "Reiki and chakra work",
    body: "Optional, energy-based practices offered in a calm setting for clients who are interested in that approach. You do not need to share any particular belief - curiosity is enough, and it is always your choice.",
  },
];

const FIRST_VISIT_STEPS = [
  {
    title: "A short conversation",
    body: "You start by talking - what brought you in, where you tend to hold tension, what kind of pressure or touch you are comfortable with. Nothing begins until you have been properly heard.",
  },
  {
    title: "Agree an approach together",
    body: "Dorothy explains the approaches she offers and suggests what might suit the day. You choose together - and you can change your mind at any point.",
  },
  {
    title: "Your session",
    body: "Unhurried and at your pace. Dorothy checks in as she works, and you are always free to ask for more or less pressure, or simply quiet.",
  },
  {
    title: "A closing check-in",
    body: "Before you leave, a few minutes to see how you feel and talk about anything you noticed - and whether you would like to come back.",
  },
];

export default function Services() {
  const config = useSiteConfig();
  const { hash } = useLocation();

  useEffect(() => {
    if (hash === "#first-visit") {
      document.getElementById("first-visit")?.scrollIntoView({ behavior: "smooth" });
    }
  }, [hash]);

  return (
    <>
      <Seo
        title="Services & Rates | Dorothy Read Healing, East Stroudsburg"
        description="One unhurried 90-minute session, shaped around you - the approaches Dorothy may draw on, package savings, and what a visit involves."
        path="/services"
      />

      <section className="mx-auto max-w-6xl px-6 pb-14 pt-12 md:pt-20">
        <div className="grid items-center gap-10 md:grid-cols-12">
          <div className="md:col-span-7">
            <p className="eyebrow">Services &amp; Rates</p>
            <h1 className="mt-4 font-serif text-5xl font-semibold leading-[1.05] tracking-tight text-primary">
              One session, shaped around you
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-foreground/90">
              Every visit is one unhurried 90-minute session. What happens inside it is shaped around
              you - the approaches below are options Dorothy may draw on, talked through together
              before anything begins.
            </p>
          </div>
          <div className="md:col-span-4 md:col-start-9">
            <img
              src="/images/reading.jpg"
              alt="Dorothy reading quietly by a window"
              className="aspect-[4/5] w-full rounded-2xl object-cover shadow-[0_24px_60px_rgba(66,82,68,0.16)]"
              loading="eager"
            />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-6 py-14 md:py-16" data-testid="services-techniques">
        <h2 className="font-serif text-4xl font-semibold tracking-tight text-primary">
          Approaches Dorothy may draw on
        </h2>
        <p className="mt-4 text-lg text-foreground/90">
          In plain language - ask Dorothy about any of them when you talk.
        </p>
        <Accordion type="single" collapsible className="mt-8">
          {TECHNIQUES.map((t) => (
            <AccordionItem key={t.id} value={t.id} data-testid={`technique-${t.id}`}>
              <AccordionTrigger className="text-left text-lg font-semibold">{t.title}</AccordionTrigger>
              <AccordionContent className="text-lg leading-relaxed">{t.body}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <section className="bg-secondary/50" data-testid="services-pricing">
        <div className="mx-auto max-w-6xl px-6 py-16 md:py-20">
          <h2 className="font-serif text-4xl font-semibold tracking-tight text-primary">Rates</h2>
          <div className="mt-10">
            <Pricing />
          </div>
        </div>
      </section>

      <section id="first-visit" className="mx-auto max-w-3xl scroll-mt-28 px-6 py-16 md:py-24" data-testid="first-visit-steps">
        <p className="eyebrow">Your first visit</p>
        <h2 className="mt-4 font-serif text-4xl font-semibold tracking-tight text-primary">
          Know exactly what to expect
        </h2>
        <p className="mt-4 text-lg leading-relaxed text-foreground/90">
          Every session is 90 minutes and $125 - deliberately unhurried, with time to talk before
          anything begins and time to settle before you leave. Questions beforehand - about clothing,
          pressure, touch or anything else - are always welcome through the schedule form.
        </p>
        <div className="mt-10 space-y-10">
          {FIRST_VISIT_STEPS.map((s, i) => (
            <div key={s.title} className="flex gap-6" data-testid={`step-${i + 1}`}>
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 border-accent font-serif text-xl font-semibold text-accent">
                {i + 1}
              </span>
              <div>
                <h3 className="font-serif text-2xl font-semibold text-primary">{s.title}</h3>
                <p className="mt-2 text-lg leading-relaxed text-foreground/90">{s.body}</p>
              </div>
            </div>
          ))}
        </div>
        <img
          src="/images/treatment-room.jpg"
          alt="Dorothy preparing fresh linen on the treatment table in her calm, naturally lit room"
          className="mt-12 aspect-[3/2] w-full rounded-2xl object-cover shadow-[0_16px_44px_rgba(66,82,68,0.14)]"
          loading="lazy"
        />

        <p className="mt-10 rounded-2xl border border-border bg-card p-6 text-base leading-relaxed text-muted-foreground" data-testid="services-disclaimer">
          {config.disclaimer}
        </p>

        <div className="mt-12 text-center">
          <BookingCta location="services" testid="services-schedule-appointment" />
        </div>
      </section>
    </>
  );
}
